/**
 * The trip fund, as a tab inside the trip screen.
 *
 * The screen renders a view of the ledger, not a copy of it: the pot, the per-member panel and the list
 * all come from one `GET /trips/:id/fund`, and every figure on screen — including the running pot after
 * each row — was computed by the server from those same rows. Nothing here adds money up. There is no
 * `reduce` over `amount_vnd`, because a second sum on this side is a second answer that can disagree
 * with the one the list shows, and the disagreement would be money.
 *
 * Three behaviours are the design's own and are worth naming:
 *
 * - **An empty ledger is zero.** A side with nothing recorded renders `0 đ` in the money slot — the pot,
 *   the three figures and the wallet chip all agree. The fact that nothing has been recorded *yet* is the
 *   empty state's sentence and its one action, not a word in a numeric slot.
 * - **An expense may over-draw the fund.** A negative pot is a number, not a warning: it is not clamped,
 *   the record is never refused, and "+ Thu quỹ" sits beside it with no threshold.
 * - **After recording, the sheet closes and the new row is brought into view** and marked once. The
 *   reason the fund gets the whole screen is to see a row in its context — its day, its neighbours, the
 *   pot left after it — and an open sheet covers exactly that.
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ChangeEvent, ReactElement } from "react";

import { copy } from "../copy.ts";
import { foldForCompare } from "../lib/capture.ts";
import { messageFor, serverReasonFor } from "../lib/errors.ts";
import { formatAmountInput, formatVnd, getAmountSuggestions, parseVndInput } from "../lib/money.ts";
import { todayIso, tripDayIndex, tripDayIso } from "../lib/tripDates.ts";
import {
  useCreateFundParticipant,
  useDeleteFundContribution,
  useDeleteFundExpense,
  useRecordFundContribution,
  useRecordFundExpense,
  useRemoveFundParticipant,
  useUpdateFundContribution,
  useUpdateFundExpense,
  useUpdateFundParticipant,
} from "../lib/queries.ts";

import type { FundEntry, TripFundResponse } from "@trippool/contracts";

/** Which sheet is open, and whether it is correcting an existing row or recording a new one. */
interface SheetState {
  mode: "expense" | "contribution";
  entry: FundEntry | null;
}

/** A field the sheet validates, named so a message and its control can be tied together by id. */
type FieldName = "description" | "amount" | "participant";

/**
 * The trip day a chosen date is, or null when it is no day of this trip.
 *
 * A day of the trip is a date between the start and the end, both inclusive. Before the start, after the
 * end, empty, or a date the calendar does not hold all answer null. A date outside the trip is **not** an
 * error: money can be paid before a trip, and the API stores such a row with no day rather than clamping
 * it to day one, which would be a fact the user never entered. `tripDayIndex` already answers null before
 * the start; this is only the caller that also bounds the far end, which it alone knows.
 */
function tripDayOf(iso: string, startDate: string, endDate: string): number | null {
  const date = iso.trim();
  if (date === "") return null;
  const index = tripDayIndex(startDate, date);
  const last = tripDayIndex(startDate, endDate);
  if (index === null || last === null || index > last) return null;
  return index;
}

/**
 * What the chosen date becomes, said in the words the ledger uses, or null when no date is chosen.
 *
 * Inside the trip it is that day, named by `dayLabel` exactly as the ledger's own day head names it, so
 * the sheet and the list cannot describe one day two ways. Outside it, the line says which side the date
 * falls on rather than leaving the day silently unset — the user sees that the app read a pre-trip
 * contribution as pre-trip, not as day one.
 */
function dayNoteFor(iso: string, startDate: string, endDate: string): string | null {
  const index = tripDayOf(iso, startDate, endDate);
  if (index !== null) return copy.expenses.dayLabel(index);
  const date = iso.trim();
  if (date === "") return null;
  if (startDate !== "" && date < startDate) return copy.expenses.dayBeforeTrip;
  if (endDate !== "" && date > endDate) return copy.expenses.dayAfterTrip;
  return null;
}

export function FundTab(props: {
  userId: string;
  tripId: string;
  /** The trip's own range, `YYYY-MM-DD`. The day control writes a trip day, so it needs the start. */
  startDate: string;
  endDate: string;
  fund: TripFundResponse;
}): ReactElement {
  const { userId, tripId, startDate, endDate, fund } = props;

  const recordExpense = useRecordFundExpense(userId, tripId);
  const recordContribution = useRecordFundContribution(userId, tripId);
  const updateContribution = useUpdateFundContribution(userId, tripId);
  const updateExpense = useUpdateFundExpense(userId, tripId);
  const deleteContribution = useDeleteFundContribution(userId, tripId);
  const deleteExpense = useDeleteFundExpense(userId, tripId);
  const createParticipant = useCreateFundParticipant(userId, tripId);
  const updateParticipant = useUpdateFundParticipant(userId, tripId);
  const removeParticipant = useRemoveFundParticipant(userId, tripId);

  const [sheet, setSheet] = useState<SheetState | null>(null);
  const [freshId, setFreshId] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  /*
   * The manage-members dialog. It is opened from the sheet's contributor row and overlays it, so the
   * member list and the contribution being recorded are visible in one place. `editingId` is the member
   * whose name is an input, `confirmingMemberId` the one whose delete has been asked about, and
   * `memberError` carries the server's own reason for a refusal — the only place a refusal can come from,
   * since the client cannot know whether a member has a transaction.
   */
  const [manageOpen, setManageOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [memberDraft, setMemberDraft] = useState("");
  const [confirmingMemberId, setConfirmingMemberId] = useState<string | null>(null);
  const [memberError, setMemberError] = useState("");

  // Form state. One field per control. `amountText` is the grouped string the field shows and is parsed
  // back to the integer on submit; `dayIso` is the date the trip day is read from.
  const [description, setDescription] = useState("");
  const [amountText, setAmountText] = useState("");
  const [note, setNote] = useState("");
  const [dayIso, setDayIso] = useState("");
  const [participantId, setParticipantId] = useState("");
  /*
   * One message per field, keyed by the field it belongs to, plus a `formError` for a failure that is not
   * any one field's (a rejected write). A message renders under its own control, which `aria-describedby`
   * points at, so the field is both visibly and audibly the one being described.
   */
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [formError, setFormError] = useState("");

  const descriptionRef = useRef<HTMLInputElement | null>(null);
  const amountRef = useRef<HTMLInputElement | null>(null);
  const participantRef = useRef<HTMLSelectElement | null>(null);
  /** Focused when the dialog opens, and when the add row's own submit refuses an empty name. */
  const memberDraftRef = useRef<HTMLInputElement | null>(null);
  /**
   * Where focus goes when the mode of a row changes under the user.
   *
   * The delete confirmation replaces the row's controls, so the button that was just activated unmounts
   * and focus would fall to `<body>` — a keyboard user would be dropped out of the dialog mid-action.
   * These two put it back: on the confirm control when the question appears, and on the close control
   * when a refusal leaves nowhere better to stand.
   */
  const confirmDeleteRef = useRef<HTMLButtonElement | null>(null);
  const closeDialogRef = useRef<HTMLButtonElement | null>(null);
  /**
   * The digits the caret had passed, held across a reformat.
   *
   * Formatting runs in `onChange`, which means React re-renders with a longer string and the browser
   * would otherwise leave the caret at its old offset — one separator to the left of where it was, which
   * is the jump this feature is famous for. `onAmountChange` records the digit count instead of the
   * offset, and the layout effect below restores it against the new string.
   */
  const caretDigitsRef = useRef<number | null>(null);

  /*
   * The list's own state: the search text, which kind is showing, which days are folded, and which
   * row's ⋯ menu is open. All four are presentation — nothing here computes money.
   */
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<"all" | "in" | "out">("all");
  const [collapsedDays, setCollapsedDays] = useState<ReadonlySet<string>>(() => new Set());
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const menuTriggerRef = useRef<HTMLButtonElement | null>(null);

  const liveParticipants = fund.participants.filter((participant) => !participant.removed);

  /*
   * The search, folded once with the app's own folding — the same rule the capture form and the places
   * list use, so `da lat` and `Đà Lạt` are one query and not two. A query the money grammar can read is
   * also tried against the amount's digits, which is why `200k` finds the 200.000 đ row.
   */
  const foldedQuery = foldForCompare(query);
  const numericQuery = parseVndInput(query);

  function matchesEntry(entry: FundEntry): boolean {
    if (foldedQuery === "") return true;
    const words = foldForCompare(`${entry.description ?? ""} ${entry.contributor_name ?? ""} ${entry.note ?? ""}`);
    if (words.includes(foldedQuery)) return true;
    return numericQuery !== null && String(Math.abs(entry.amount_vnd)).includes(String(numericQuery));
  }

  // Counts are over the search alone, so each filter says how much is behind it before it is pressed.
  const searched = fund.entries.filter(matchesEntry);
  const counts = {
    all: searched.length,
    in: searched.filter((entry) => entry.kind === "contribution").length,
    out: searched.filter((entry) => entry.kind === "expense").length,
  };
  const visible =
    filter === "all"
      ? searched
      : searched.filter((entry) => entry.kind === (filter === "in" ? "contribution" : "expense"));

  function openSheet(mode: SheetState["mode"], entry: FundEntry | null): void {
    setSheet({ mode, entry });
    setDescription(entry?.description ?? "");
    setAmountText(entry === null ? "" : formatAmountInput(String(entry.amount_vnd)));
    setNote(entry?.note ?? "");
    /*
     * A new entry opens on today, the user's own local day, whether or not the trip has started — money
     * paid before a trip is ordinary. An edit opens on the day the row already has, and on nothing when it
     * has none — defaulting an edited row to today would silently give it a day it never had.
     */
    const entryDay = entry?.day_index ?? null;
    setDayIso(
      entry === null
        ? todayIso()
        : entryDay === null
          ? ""
          : (tripDayIso(startDate, entryDay) ?? ""),
    );
    setParticipantId(entry?.participant_id ?? liveParticipants[0]?.id ?? "");
    setManageOpen(false);
    setEditingId(null);
    setMemberDraft("");
    setConfirmingMemberId(null);
    setMemberError("");
    setFieldErrors({});
    setFormError("");
    setConfirmingId(null);
    // An edit or a record opens a sheet over the list; the menu it was reached from closes with it.
    setMenuFor(null);
  }

  function dayKeyOf(entry: FundEntry): string {
    return entry.day_index === null ? "none" : `d${String(entry.day_index)}`;
  }

  function toggleDay(key: string): void {
    setCollapsedDays((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  /*
   * The row just recorded is brought into view, and it is the row's own `id` that anchors it.
   *
   * `behavior: "auto"` under `prefers-reduced-motion` rather than `"smooth"`: the CSS ring is already
   * static there, and a smooth scroll is motion the user asked not to have.
   */
  useEffect(() => {
    if (freshId === null) return;
    // A recorded row's day opens, or the row would be scrolled to inside a folded group.
    const fresh = fund.entries.find((entry) => entry.id === freshId);
    if (fresh !== undefined) {
      const key = dayKeyOf(fresh);
      setCollapsedDays((current) => {
        if (!current.has(key)) return current;
        const next = new Set(current);
        next.delete(key);
        return next;
      });
    }
    const row = document.getElementById(`fund-row-${freshId}`);
    // The row does not exist on the render that sets `freshId`: the list is rebuilt from the refetched
    // fund, so this returns and the effect runs again when `fund` arrives. Scrolling once here would be
    // scrolling before there is anything to scroll to.
    if (row === null) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    row.scrollIntoView({ block: "center", behavior: reduce ? "auto" : "smooth" });
  }, [freshId, fund]);

  useEffect(() => {
    // While the manage-members dialog is over the sheet, Escape belongs to the dialog, not to the sheet.
    if (sheet === null || manageOpen) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setSheet(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [sheet, manageOpen]);

  /*
   * The manage-members dialog: Escape closes it and focus lands on its add row, which is the one control
   * a keyboard user came for. The dialog is the only thing on top, so it owns Escape while it is open.
   */
  useEffect(() => {
    if (!manageOpen) return;
    memberDraftRef.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setManageOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [manageOpen]);

  /* The delete confirmation takes focus when it appears, rather than leaving it on a removed button. */
  useEffect(() => {
    if (confirmingMemberId !== null) confirmDeleteRef.current?.focus();
  }, [confirmingMemberId]);

  /*
   * The row menu closes on Escape and on a click outside it. Escape hands focus back to the trigger it
   * was opened from, which is the control the user was on before the menu existed.
   */
  useEffect(() => {
    if (menuFor === null) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      setMenuFor(null);
      menuTriggerRef.current?.focus();
    };
    const onPointer = (event: MouseEvent): void => {
      const target = event.target as HTMLElement | null;
      if (target?.closest(".row__menu") === null) setMenuFor(null);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [menuFor]);

  /* `/` focuses the search, the shortcut a list with a search field usually has. */
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "/") return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target?.isContentEditable === true) return;
      event.preventDefault();
      searchRef.current?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const busy =
    recordExpense.isPending ||
    recordContribution.isPending ||
    updateContribution.isPending ||
    updateExpense.isPending ||
    createParticipant.isPending ||
    updateParticipant.isPending ||
    removeParticipant.isPending;

  /*
   * The caret is put back where the digits say it was, after every reformat.
   *
   * A layout effect rather than `useEffect`: it runs before the browser paints, so the caret is never
   * shown at the wrong offset for a frame. It acts only when `onAmountChange` recorded a digit count, so
   * a chip's own write of the value does not move a caret the user was not holding.
   */
  useLayoutEffect(() => {
    const input = amountRef.current;
    const wanted = caretDigitsRef.current;
    if (input === null || wanted === null) return;
    caretDigitsRef.current = null;
    /*
     * Walk the new string to the position after the same number of digits. Counting digits rather than
     * characters is what makes the separator the field inserts invisible to the caret: one more `.`
     * appears, and the caret ends on the far side of the same digit it was beside.
     */
    let position = 0;
    for (let seen = 0; position < input.value.length && seen < wanted; position += 1) {
      if (/\d/.test(input.value[position] ?? "")) seen += 1;
    }
    input.setSelectionRange(position, position);
  }, [amountText]);

  /**
   * The amount field's change handler: reformat, and remember the caret in digits.
   *
   * Reading `selectionStart` here — before React re-renders — is the whole trick. `caretDigitsRef` holds
   * how many digits preceded the caret in what was typed, and the layout effect turns that back into an
   * offset in the grouped string.
   */
  function onAmountChange(event: ChangeEvent<HTMLInputElement>): void {
    const value = event.target.value;
    const caret = event.target.selectionStart ?? value.length;
    caretDigitsRef.current = value.slice(0, caret).replace(/\D/g, "").length;
    clearError("amount");
    setAmountText(formatAmountInput(value));
  }

  /** A suggestion chip replaces the field's value and leaves the caret at its end. */
  function applySuggestion(amount: number): void {
    caretDigitsRef.current = String(amount).length;
    clearError("amount");
    setAmountText(formatAmountInput(String(amount)));
    amountRef.current?.focus();
  }

  /** Clear one field's message as the user edits it, so a fixed field stops reading as broken. */
  function clearError(field: FieldName): void {
    setFieldErrors((current) => {
      if (current[field] === undefined) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  }

  /**
   * Refuse the submit: one message, under its own field, and focus moved there.
   *
   * The message and the control are tied by `aria-describedby` in the markup, so a screen reader reads it
   * as the field's description rather than leaving it a sentence somewhere on the sheet.
   */
  function fail(field: FieldName, message: string): void {
    setFieldErrors({ [field]: message });
    setFormError("");
    focusRef(field).focus();
  }

  function focusRef(field: FieldName): HTMLElement {
    if (field === "description") return descriptionRef.current ?? document.body;
    if (field === "participant") return participantRef.current ?? document.body;
    return amountRef.current ?? document.body;
  }

  /**
   * Open the manage-members dialog on a clean slate: nothing editing, nothing being confirmed, and no
   * message left over from a previous visit.
   */
  function openManageMembers(): void {
    setEditingId(null);
    setEditName("");
    setMemberDraft("");
    setConfirmingMemberId(null);
    setMemberError("");
    setManageOpen(true);
  }

  /**
   * Add a person from the dialog's add row and select them in the sheet behind it.
   *
   * Selecting the new name is the point of opening this from the contributor row: it is the one the user
   * wants next, and they should not have to find it in the select a second time.
   */
  async function addMember(): Promise<void> {
    const name = memberDraft.trim();
    if (name === "") {
      setMemberError(copy.expenses.needName);
      memberDraftRef.current?.focus();
      return;
    }
    try {
      const created = await createParticipant.mutateAsync(name);
      setParticipantId(created.id);
      setMemberDraft("");
      setMemberError("");
      clearError("participant");
    } catch (error: unknown) {
      setMemberError(serverReasonFor(error));
    }
  }

  /** Turn a member's row into an inline input, prefilled with their current name. */
  function beginEditMember(participantIdToEdit: string, currentName: string): void {
    setEditingId(participantIdToEdit);
    setEditName(currentName);
    setConfirmingMemberId(null);
    setMemberError("");
  }

  /** Save the inline name. A duplicate folded name is the server's 409, shown as it comes. */
  async function saveMemberName(): Promise<void> {
    if (editingId === null) return;
    const name = editName.trim();
    if (name === "") {
      setMemberError(copy.expenses.needName);
      return;
    }
    try {
      await updateParticipant.mutateAsync({ participantId: editingId, name });
      setEditingId(null);
      setMemberError("");
    } catch (error: unknown) {
      setMemberError(serverReasonFor(error));
    }
  }

  /**
   * Delete a member. This does not decide whether they may go: it asks the server, and shows the reason
   * it gives. The confirmation in front of it is a question, not a guard — only the server knows whether
   * the member has a transaction, so a client-side check would be a lie the next read would contradict.
   */
  async function removeMember(participantIdToRemove: string): Promise<void> {
    try {
      await removeParticipant.mutateAsync(participantIdToRemove);
      setConfirmingMemberId(null);
      setMemberError("");
      // The select behind the dialog must never point at a member who is gone.
      if (participantId === participantIdToRemove) {
        const next = liveParticipants.find((member) => member.id !== participantIdToRemove);
        setParticipantId(next?.id ?? "");
      }
    } catch (error: unknown) {
      setConfirmingMemberId(null);
      setMemberError(serverReasonFor(error));
      closeDialogRef.current?.focus();
    }
  }

  async function submit(): Promise<void> {
    const mode = sheet?.mode ?? "expense";
    const entry = sheet?.entry ?? null;
    const trimmedDescription = description.trim();
    const amount = parseVndInput(amountText);
    /*
     * The trip day the chosen date is, or null when it is no day of this trip. A date outside the trip is
     * not a refusal — the row is stored with no day, which is what the date honestly is.
     */
    const dayIndex = tripDayOf(dayIso, startDate, endDate);

    if (mode === "expense" && trimmedDescription === "") {
      fail("description", copy.expenses.needDescription);
      return;
    }
    if (mode === "contribution" && entry === null && participantId === "") {
      fail("participant", copy.expenses.needParticipant);
      return;
    }
    if (amount === null || amount <= 0) {
      fail("amount", copy.expenses.needAmount);
      return;
    }
    setFieldErrors({});

    const trimmedNote = note.trim();
    const noteValue = trimmedNote === "" ? null : trimmedNote;

    try {
      if (mode === "expense") {
        if (entry === null) {
          const created = await recordExpense.mutateAsync({
            description: trimmedDescription,
            amount_vnd: amount,
            day_index: dayIndex,
            note: noteValue,
          });
          setFreshId(created.id);
        } else {
          await updateExpense.mutateAsync({
            entryId: entry.id,
            amount_vnd: amount,
            description: trimmedDescription,
            day_index: dayIndex,
            note: noteValue,
          });
        }
      } else {
        if (entry === null) {
          const created = await recordContribution.mutateAsync({
            participant_id: participantId,
            amount_vnd: amount,
            day_index: dayIndex,
            note: noteValue,
          });
          setFreshId(created.id);
        } else {
          await updateContribution.mutateAsync({
            entryId: entry.id,
            amount_vnd: amount,
            day_index: dayIndex,
            note: noteValue,
          });
        }
      }
      setSheet(null);
    } catch (error: unknown) {
      setFormError(messageFor(error));
    }
  }

  async function removeEntry(entry: FundEntry): Promise<void> {
    setConfirmingId(null);
    setMenuFor(null);
    try {
      if (entry.kind === "contribution") await deleteContribution.mutateAsync(entry.id);
      else await deleteExpense.mutateAsync(entry.id);
    } catch {
      // A failed delete leaves the row in place and the next read shows the truth; nothing to say here
      // that the list itself does not already say by still showing the row.
    }
  }

  /*
   * The ledger as day groups, in the order the API returned — day, then record order, then the rows with
   * no day. The grouping is presentation only: the rows and their running figures are the server's.
   */
  const groups: Array<{ key: string; dayIndex: number | null; entries: FundEntry[] }> = [];
  for (const entry of visible) {
    const key = dayKeyOf(entry);
    const last = groups[groups.length - 1];
    if (last !== undefined && last.key === key) last.entries.push(entry);
    else groups.push({ key, dayIndex: entry.day_index, entries: [entry] });
  }

  const plainPot = fund.pot_vnd === null || fund.pot_vnd < 0;
  // Booleans, so the only quoted strings in a class attribute are class names.
  const isExpenseMode = sheet?.mode === "expense";
  const isContributionMode = sheet?.mode === "contribution";

  /*
   * The two things the amount field computes: the chips under it, derived from what is typed, and the
   * record button's label, which names the figure it would write. Both read the same parse the submit
   * does, so the button and the row cannot disagree about the amount.
   */
  const suggestions = getAmountSuggestions(amountText);
  const amountPreview = parseVndInput(amountText);
  const dayNote = dayNoteFor(dayIso, startDate, endDate);
  const saveLabel =
    amountPreview !== null && amountPreview > 0
      ? copy.expenses.saveWithAmount(formatVnd(amountPreview))
      : copy.expenses.save;

  // One filter per kind. The count the current search leaves it is part of the label, in parentheses.
  const filterOptions: ReadonlyArray<{ value: "all" | "in" | "out"; text: string }> = [
    { value: "all", text: copy.expenses.filterAll(counts.all) },
    { value: "in", text: copy.expenses.filterIn(counts.in) },
    { value: "out", text: copy.expenses.filterOut(counts.out) },
  ];

  return (
    <div className="fund-body">
      {/*
        The pot, then the two verbs, in that order and in the same card — but only once the ledger has a
        row. Those verbs are the fund's add door on a non-empty ledger; while the ledger is empty the
        empty state below is the door, and rendering this pair as well would put two ways to the same
        sheet on one screen. The figure is the prominent element and the buttons sit directly under it —
        one row for the balance, one for the actions — because an action on its own line leaves a gap in
        the middle and crowds the trailing edge.
      */}
      <div className={plainPot ? "pot pot--plain" : "pot"}>
        <span className="pot__label">{copy.expenses.potLabel}</span>
        <strong className={fund.pot_vnd === null ? "pot__value pot__value--none" : "pot__value"}>
          {fund.pot_vnd === null ? copy.expenses.zero : formatVnd(fund.pot_vnd)}
        </strong>
        {fund.entries.length > 0 && (
          <div className="pot__actions">
            <button
              className="button button--ghost"
              type="button"
              onClick={() => openSheet("contribution", null)}
            >
              {copy.expenses.topUp}
            </button>
            <button className="button button--ghost" type="button" onClick={() => openSheet("expense", null)}>
              {copy.expenses.recordExpense}
            </button>
          </div>
        )}
        {fund.pot_vnd !== null && fund.pot_vnd < 0 && <p className="pot__line">{copy.expenses.negativeLine}</p>}
      </div>

      {/*
        The three figures, as one thin borderless row: a glyph, the amount, and the small label under it.
        The boxed cards this replaces cost 93px of a phone to say three short things, and a box around
        each number made them read as three separate widgets rather than one line of the fund's shape.
      */}
      <div className="fund-figures">
        <div className="fund-figure">
          <span className="fund-figure__icon material-symbols-outlined" aria-hidden="true">
            savings
          </span>
          <span
            className={
              fund.contributed_vnd === null ? "fund-figure__value fund-figure__value--none" : "fund-figure__value"
            }
          >
            {fund.contributed_vnd === null ? copy.expenses.zero : formatVnd(fund.contributed_vnd)}
          </span>
          <span className="fund-figure__label">{copy.expenses.contributed}</span>
        </div>
        <div className="fund-figure">
          <span className="fund-figure__icon material-symbols-outlined" aria-hidden="true">
            payments
          </span>
          <span
            className={
              fund.spent_vnd === null ? "fund-figure__value fund-figure__value--none" : "fund-figure__value"
            }
          >
            {fund.spent_vnd === null ? copy.expenses.zero : formatVnd(fund.spent_vnd)}
          </span>
          <span className="fund-figure__label">{copy.expenses.spent}</span>
        </div>
        <div className="fund-figure">
          <span className="fund-figure__icon material-symbols-outlined" aria-hidden="true">
            format_list_numbered
          </span>
          <span className="fund-figure__value">{String(fund.entry_count)}</span>
          <span className="fund-figure__label">{copy.expenses.entryCount}</span>
        </div>
      </div>

      {/*
        The search and the filters, in one block: the count on each tab is the search's own count, so the
        control that produces the number sits directly above it and the two read as one instrument. The
        block is spaced tighter inside than the sections around it, so the eye groups them.
      */}
      <div className="fund-tools">
        {/*
          The search: a glyph, a labelled field, and a clear control that exists only while there is text.
          The placeholder names the three words it matches, because which of them the user means is theirs
          to know. `/` focuses it, which is the one keyboard habit this list can assume.
        */}
        <div className="fund-search">
          <label className="visually-hidden" htmlFor="fund-search">
            {copy.expenses.searchLabel}
          </label>
          <span className="fund-search__glyph material-symbols-outlined" aria-hidden="true">
            search
          </span>
          <input
            className="field__input fund-search__input"
            id="fund-search"
            ref={searchRef}
            type="search"
            inputMode="search"
            placeholder={copy.expenses.searchPlaceholder}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {query !== "" && (
            <button
              className="fund-search__clear"
              type="button"
              aria-label={copy.expenses.searchClear}
              onClick={() => {
                setQuery("");
                searchRef.current?.focus();
              }}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                close
              </span>
            </button>
          )}
        </div>

        {/*
          The three filters, as a radio group rather than a second tab list: the screen's own tabs are in
          the app bar, and a filter is a state with a selected answer, not another destination. Each
          carries the count the current search leaves it, so the numbers stay true while typing.
        */}
        <div className="chips" role="radiogroup" aria-label={copy.expenses.filterLabel}>
          {filterOptions.map((option) => (
            <button
              key={option.value}
              className={filter === option.value ? "chip chip--on" : "chip"}
              type="button"
              role="radio"
              aria-checked={filter === option.value}
              onClick={() => setFilter(option.value)}
            >
              {option.text}
            </button>
          ))}
        </div>
      </div>

      {fund.entries.length === 0 ? (
        /*
          Nothing recorded: an empty wallet, the one sentence that says so, and the action that starts the
          ledger — the fund's only add door while it is empty, since the balance card above keeps its verb
          pair for a ledger that has rows. The sentence carries what used to live in the money slots — that
          nothing has been recorded yet — so the slots themselves can be the number they are.
        */
        <div className="state">
          <span className="state__glyph material-symbols-outlined" aria-hidden="true">
            account_balance_wallet
          </span>
          <p className="state__desc">{copy.expenses.emptyList}</p>
          <p className="field__help">{copy.expenses.emptyHint}</p>
          <button className="button button--primary" type="button" onClick={() => openSheet("expense", null)}>
            {copy.expenses.emptyAction}
          </button>
        </div>
      ) : groups.length === 0 ? (
        <div className="state">
          <p className="state__desc">{copy.expenses.noMatch(query)}</p>
          <button
            className="button button--ghost"
            type="button"
            onClick={() => {
              setQuery("");
              setFilter("all");
            }}
          >
            {copy.expenses.clearFilters}
          </button>
        </div>
      ) : (
        groups.map((group) => {
          const last = group.entries[group.entries.length - 1];
          const folded = collapsedDays.has(group.key);
          return (
            <section className="daygroup" key={group.key}>
              <h4 className="daygroup__head">
                {/*
                  The head folds the day. The figure that survives folding is rendered only while folded:
                  an open day's last row already states the pot it leaves, and printing it twice would be
                  two places for one number to live.
                */}
                <button
                  className="daygroup__toggle"
                  type="button"
                  aria-expanded={!folded}
                  aria-controls={`fund-day-${group.key}`}
                  onClick={() => toggleDay(group.key)}
                >
                  <span className="daygroup__caret material-symbols-outlined" aria-hidden="true">
                    expand_more
                  </span>
                  <span className="daygroup__name">
                    {group.dayIndex === null ? copy.expenses.dayNone : copy.expenses.dayLabel(group.dayIndex)}
                  </span>
                </button>
                {folded && last !== undefined && (
                  <span className="daygroup__sub">{copy.expenses.dayRemaining(formatVnd(last.pot_after))}</span>
                )}
              </h4>
              {!folded && (
                <ol className="cards" id={`fund-day-${group.key}`}>
                  {group.entries.map((entry) => {
                    const isFresh = entry.id === freshId;
                    // Booleans, so the only quoted strings in a class attribute are class names.
                    const isContribution = entry.kind === "contribution";
                    const name = (isContribution ? entry.contributor_name : entry.description) ?? "";
                    const initial = isContribution ? name.trim().charAt(0).toUpperCase() : "";
                    return (
                      <li key={entry.id} id={`fund-row-${entry.id}`} className={isFresh ? "card card--fresh" : "card"}>
                        <div className="row">
                          <div className="row__lead">
                            {initial !== "" ? (
                              <span className="row__avatar row__avatar--in" aria-hidden="true">
                                {initial}
                              </span>
                            ) : (
                              <span
                                className={
                                  isContribution
                                    ? "row__avatar row__avatar--in material-symbols-outlined"
                                    : "row__avatar row__avatar--out material-symbols-outlined"
                                }
                                aria-hidden="true"
                              >
                                {isContribution ? "person" : "receipt_long"}
                              </span>
                            )}
                            <div className="row__main">
                              <span className="row__desc">{name}</span>
                              <span className="row__meta">
                                <span className={isContribution ? "badge badge--in" : "badge badge--out"}>
                                  {isContribution ? copy.expenses.kindContribution : copy.expenses.kindExpense}
                                </span>
                                <span className="row__day">
                                  {entry.day_index === null
                                    ? copy.expenses.dayNone
                                    : copy.expenses.dayLabel(entry.day_index)}
                                </span>
                                {entry.edited_at !== null && (
                                  <span className="badge badge--quiet">{copy.expenses.edited}</span>
                                )}
                                {isFresh && <span className="badge">{copy.expenses.fresh}</span>}
                              </span>
                              {entry.note !== null && <span className="row__note">{entry.note}</span>}
                            </div>
                          </div>
                          <div className="row__side">
                            <span
                              className={
                                isContribution ? "row__amount row__amount--in" : "row__amount row__amount--out"
                              }
                            >
                              {formatVnd(entry.amount_vnd)}
                            </span>
                            <span className="row__pot">{copy.expenses.potAfter(formatVnd(entry.pot_after))}</span>
                            {/*
                              The two verbs live here, not on the row: a row that shows Sửa and Xoá beside
                              every amount puts a destructive tap one slip from a hand that meant to edit.
                            */}
                            <div className="row__menu">
                              <button
                                className="row__menu-trigger"
                                type="button"
                                aria-label={copy.expenses.rowMenu(name)}
                                aria-haspopup="menu"
                                aria-expanded={menuFor === entry.id}
                                onClick={(event) => {
                                  menuTriggerRef.current = event.currentTarget;
                                  setConfirmingId(null);
                                  setMenuFor(menuFor === entry.id ? null : entry.id);
                                }}
                              >
                                <span className="material-symbols-outlined" aria-hidden="true">
                                  more_vert
                                </span>
                              </button>
                              {menuFor === entry.id && (
                                <div className="row__menu-list" role="menu">
                                  <button
                                    className="row__menu-item"
                                    type="button"
                                    role="menuitem"
                                    onClick={() => openSheet(entry.kind, entry)}
                                  >
                                    {copy.expenses.edit}
                                  </button>
                                  {confirmingId === entry.id ? (
                                    <button
                                      className="row__menu-item row__menu-item--danger"
                                      type="button"
                                      role="menuitem"
                                      disabled={busy}
                                      onClick={() => void removeEntry(entry)}
                                    >
                                      {copy.expenses.confirmRemove}
                                    </button>
                                  ) : (
                                    <button
                                      className="row__menu-item row__menu-item--danger"
                                      type="button"
                                      role="menuitem"
                                      onClick={() => setConfirmingId(entry.id)}
                                    >
                                      {copy.expenses.remove}
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </section>
          );
        })
      )}

      {fund.participants.length > 0 && (
        <section className="daygroup">
          <h4 className="daygroup__head">
            <span className="daygroup__name">{copy.expenses.membersHeading}</span>
          </h4>
          <ul className="cards">
            {fund.participants.map((participant) => (
              <li className="card member" key={participant.id}>
                <span className="member__name">{participant.name}</span>
                <span
                  className={
                    participant.contributed_vnd === null ? "member__amount member__amount--none" : "member__amount"
                  }
                >
                  {participant.contributed_vnd === null
                    ? copy.expenses.zero
                    : formatVnd(participant.contributed_vnd)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {sheet !== null && (
        <div
          className="sheetwrap"
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget) setSheet(null);
          }}
        >
          <div
            className="sheet"
            role="dialog"
            aria-modal="true"
            aria-label={sheet.entry === null ? copy.expenses.sheetNewTitle : copy.expenses.sheetEditTitle}
          >
            <div className="sheet__body">
              <span className="sheet__title">
                {sheet.entry === null ? copy.expenses.sheetNewTitle : copy.expenses.sheetEditTitle}
              </span>

              {sheet.entry === null && (
                <div className="chips sheet__modes" role="group" aria-label={copy.expenses.sheetNewTitle}>
                  <button
                    className={isExpenseMode ? "chip chip--on" : "chip"}
                    type="button"
                    aria-pressed={isExpenseMode}
                    onClick={() => {
                      setSheet({ mode: "expense", entry: null });
                      setFieldErrors({});
                      setFormError("");
                    }}
                  >
                    {copy.expenses.modeExpense}
                  </button>
                  <button
                    className={isContributionMode ? "chip chip--on" : "chip"}
                    type="button"
                    aria-pressed={isContributionMode}
                    onClick={() => {
                      setSheet({ mode: "contribution", entry: null });
                      setFieldErrors({});
                      setFormError("");
                    }}
                  >
                    {copy.expenses.modeContribution}
                  </button>
                </div>
              )}

              {sheet.mode === "expense" ? (
                <div className="field">
                  <label className="field__label" htmlFor="fund-description">
                    {copy.expenses.fieldDescription}
                  </label>
                  <input
                    className="field__input"
                    id="fund-description"
                    ref={descriptionRef}
                    type="text"
                    value={description}
                    aria-invalid={fieldErrors.description !== undefined}
                    aria-describedby={fieldErrors.description !== undefined ? "fund-description-error" : undefined}
                    onChange={(event) => {
                      clearError("description");
                      setDescription(event.target.value);
                    }}
                  />
                  {fieldErrors.description !== undefined && (
                    <p className="field__error" id="fund-description-error" role="alert">
                      {fieldErrors.description}
                    </p>
                  )}
                </div>
              ) : sheet.entry === null ? (
                <>
                  {/*
                    The contributor, and beside it the door to managing the members behind it. The icon
                    opens a `Quản lý thành viên` dialog that overlays this sheet, so adding a person, renaming
                    one and removing one all live in the same place — and the contribution being recorded
                    stays visible underneath. A person added there is selected here the moment they exist.
                  */}
                  <div className="field">
                    <label className="field__label" htmlFor="fund-participant">
                      {copy.expenses.fieldParticipant}
                    </label>
                    <div className="field__row">
                      <select
                        className="field__input"
                        id="fund-participant"
                        ref={participantRef}
                        value={participantId}
                        aria-invalid={fieldErrors.participant !== undefined}
                        aria-describedby={fieldErrors.participant !== undefined ? "fund-participant-error" : undefined}
                        onChange={(event) => {
                          clearError("participant");
                          setParticipantId(event.target.value);
                        }}
                      >
                        {liveParticipants.length === 0 && <option value="">{copy.expenses.noParticipant}</option>}
                        {liveParticipants.map((participant) => (
                          <option key={participant.id} value={participant.id}>
                            {participant.name}
                          </option>
                        ))}
                      </select>
                      <button
                        className="button button--icon"
                        type="button"
                        aria-label={copy.expenses.manageMembers}
                        aria-haspopup="dialog"
                        aria-expanded={manageOpen}
                        onClick={openManageMembers}
                      >
                        <span className="material-symbols-outlined" aria-hidden="true">
                          manage_accounts
                        </span>
                      </button>
                    </div>
                    {fieldErrors.participant !== undefined && (
                      <p className="field__error" id="fund-participant-error" role="alert">
                        {fieldErrors.participant}
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <p className="field__help">{copy.expenses.fieldParticipant}</p>
              )}

              <div className="field">
                <label className="field__label" htmlFor="fund-amount">
                  {copy.expenses.fieldAmount}
                </label>
                <input
                  className="field__input"
                  id="fund-amount"
                  ref={amountRef}
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  value={amountText}
                  aria-invalid={fieldErrors.amount !== undefined}
                  aria-describedby={fieldErrors.amount !== undefined ? "fund-amount-error" : undefined}
                  onChange={onAmountChange}
                />
                {/*
                  Amounts derived from what is typed, under the field they belong to — never a fact about the
                  trip. An empty list renders nothing: at and above 10.000.000 the cap leaves no suggestion,
                  and an empty strip would read as a row that failed to load.
                */}
                {suggestions.length > 0 && (
                  <div className="chips" role="group" aria-label={copy.expenses.amountSuggestions}>
                    {suggestions.map((suggestion) => (
                      <button
                        key={suggestion}
                        className="chip"
                        type="button"
                        onClick={() => applySuggestion(suggestion)}
                      >
                        {formatVnd(suggestion)}
                      </button>
                    ))}
                  </div>
                )}
                {fieldErrors.amount !== undefined && (
                  <p className="field__error" id="fund-amount-error" role="alert">
                    {fieldErrors.amount}
                  </p>
                )}
              </div>

              <div className="field">
                <label className="field__label" htmlFor="fund-day">
                  {copy.expenses.fieldDay}
                </label>
                {/*
                  The date control imposes no trip range: money can be paid before the trip or after it,
                  and the browser's own date validity is the only bound. What the chosen date becomes is
                  said underneath, so a date outside the trip is explained rather than silently unset.
                */}
                <input
                  className="field__input"
                  id="fund-day"
                  type="date"
                  value={dayIso}
                  aria-describedby={dayNote === null ? undefined : "fund-day-note"}
                  onChange={(event) => setDayIso(event.target.value)}
                />
                {dayNote !== null && (
                  <p className="field__help" id="fund-day-note">
                    {dayNote}
                  </p>
                )}
              </div>

              <div className="field">
                <label className="field__label" htmlFor="fund-note">
                  {copy.expenses.fieldNote}
                </label>
                <input
                  className="field__input"
                  id="fund-note"
                  type="text"
                  placeholder={copy.expenses.fieldNotePlaceholder}
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                />
              </div>

              {formError !== "" && (
                <p className="field__error" role="alert">
                  {formError}
                </p>
              )}
            </div>

            <div className="sheet__footer">
              <button className="button button--ghost button--save" type="button" onClick={() => setSheet(null)}>
                {copy.expenses.cancel}
              </button>
              <button
                className="button button--primary button--save"
                type="button"
                disabled={busy}
                onClick={() => void submit()}
              >
                {sheet.entry === null ? saveLabel : copy.expenses.saveEdit}
              </button>
            </div>
          </div>
        </div>
      )}

      {/*
        The manage-members dialog, over the sheet it was opened from. Every action here speaks through the
        server: a rename that collides with an existing name is the API's 409, and a delete is refused by
        the API when the member has any transaction — the sentence shown is the response's own reason, not
        one this component derived. The list is the live members; a removed one is not a member any more.
      */}
      {manageOpen && sheet !== null && (
        <div
          className="memberdialogwrap"
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget) setManageOpen(false);
          }}
        >
          <div className="memberdialog" role="dialog" aria-modal="true" aria-labelledby="fund-members-title">
            <div className="memberdialog__body">
              <h2 className="memberdialog__title" id="fund-members-title">
                {copy.expenses.manageMembers}
              </h2>
              <ul className="memberdialog__list">
                {liveParticipants.map((participant) =>
                  editingId === participant.id ? (
                    <li className="memberrow" key={participant.id}>
                      <input
                        className="field__input memberrow__input"
                        type="text"
                        aria-label={copy.expenses.memberEditLabel(participant.name)}
                        value={editName}
                        onChange={(event) => {
                          setEditName(event.target.value);
                          setMemberError("");
                        }}
                        onKeyDown={(event) => {
                          if (event.key !== "Enter") return;
                          event.preventDefault();
                          void saveMemberName();
                        }}
                      />
                      <button
                        className="button button--icon"
                        type="button"
                        disabled={busy}
                        aria-label={copy.expenses.memberSaveLabel(participant.name)}
                        onClick={() => void saveMemberName()}
                      >
                        <span className="material-symbols-outlined" aria-hidden="true">
                          check
                        </span>
                      </button>
                    </li>
                  ) : confirmingMemberId === participant.id ? (
                    <li className="memberrow" key={participant.id}>
                      <span className="memberrow__question" role="status">
                        {copy.expenses.memberDeleteQuestion(participant.name)}
                      </span>
                      <div className="memberrow__actions">
                        <button
                          className="button button--icon button--danger"
                          type="button"
                          ref={confirmDeleteRef}
                          disabled={busy}
                          aria-label={copy.expenses.memberDeleteConfirm}
                          onClick={() => void removeMember(participant.id)}
                        >
                          <span className="material-symbols-outlined" aria-hidden="true">
                            delete
                          </span>
                        </button>
                        <button
                          className="button button--icon"
                          type="button"
                          aria-label={copy.expenses.cancel}
                          onClick={() => setConfirmingMemberId(null)}
                        >
                          <span className="material-symbols-outlined" aria-hidden="true">
                            close
                          </span>
                        </button>
                      </div>
                    </li>
                  ) : (
                    <li className="memberrow" key={participant.id}>
                      <span className="memberrow__name">{participant.name}</span>
                      <div className="memberrow__actions">
                        <button
                          className="button button--icon"
                          type="button"
                          aria-label={copy.expenses.memberEditLabel(participant.name)}
                          onClick={() => beginEditMember(participant.id, participant.name)}
                        >
                          <span className="material-symbols-outlined" aria-hidden="true">
                            edit
                          </span>
                        </button>
                        <button
                          className="button button--icon"
                          type="button"
                          aria-label={copy.expenses.memberDeleteLabel(participant.name)}
                          onClick={() => {
                            setEditingId(null);
                            setMemberError("");
                            setConfirmingMemberId(participant.id);
                          }}
                        >
                          <span className="material-symbols-outlined" aria-hidden="true">
                            delete
                          </span>
                        </button>
                      </div>
                    </li>
                  ),
                )}
                <li className="memberrow">
                  <label className="visually-hidden" htmlFor="fund-member-draft">
                    {copy.expenses.memberNameLabel}
                  </label>
                  <input
                    className="field__input memberrow__input"
                    id="fund-member-draft"
                    ref={memberDraftRef}
                    type="text"
                    value={memberDraft}
                    onChange={(event) => {
                      setMemberDraft(event.target.value);
                      setMemberError("");
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter") return;
                      event.preventDefault();
                      void addMember();
                    }}
                  />
                  <button
                    className="button"
                    type="button"
                    disabled={busy}
                    onClick={() => void addMember()}
                  >
                    {copy.expenses.addPersonConfirm}
                  </button>
                </li>
              </ul>
              {memberError !== "" && (
                <p className="field__error" role="alert">
                  {memberError}
                </p>
              )}
            </div>
            <div className="memberdialog__footer">
              <button
                className="button button--ghost"
                type="button"
                ref={closeDialogRef}
                onClick={() => setManageOpen(false)}
              >
                {copy.expenses.manageMembersClose}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
